import React from 'react';
import { useFormContext } from 'react-hook-form';
import { FiAlertCircle } from 'react-icons/fi';
import { NumericFormat } from 'react-number-format';

const FormField = ({
  name,
  label,
  type = 'text',
  placeholder = '',
  options = [],
  required = false,
  helpText = '',
  validation = {},
  disabled = false,
  className = '',
  thousandSeparator = ".",
  decimalSeparator = ",",
  suffix = "",
  maxDecimalValue = 1,
}) => {
  const { register, formState: { errors }, control, setValue, watch } = useFormContext();
  
  // Configurar as regras de validação
  const rules = {
    ...validation,
  };
  
  // Se o campo for obrigatório, adicionar a regra required
  if (required) {
    rules.required = `${label} é obrigatório`;
  }
  
  // Obter a mensagem de erro, se houver
  const errorMessage = errors[name]?.message;
  
  // Renderizar o campo de acordo com o tipo
  const renderField = () => {
    switch (type) {
      case 'numeric':
        return (
          <NumericFormat
            id={name}
            name={name}
            value={watch(name)}
            onValueChange={(values) => setValue(name, values.floatValue)}
            thousandSeparator={thousandSeparator}
            decimalSeparator={decimalSeparator}
            allowNegative={false}
            placeholder={placeholder}
            disabled={disabled}
            suffix={suffix}
            className={`input ${errorMessage ? 'border-red-500 focus:ring-red-500' : ''}`}
          />
        );
        
      case 'textarea':
        return (
          <textarea
            id={name}
            {...register(name, rules)}
            placeholder={placeholder}
            disabled={disabled}
            className={`input min-h-[100px] ${errorMessage ? 'border-red-500 focus:ring-red-500' : ''}`}
          />
        );
        
      case 'select':
        return (
          <select
            id={name}
            {...register(name, rules)}
            disabled={disabled}
            className={`input ${errorMessage ? 'border-red-500 focus:ring-red-500' : ''}`}
          >
            <option value="">Selecione uma opção</option>
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );
        
      case 'radio':
        return (
          <div className="space-y-2">
            {options.map((option) => (
              <div key={option.value} className="flex items-center">
                <input
                  id={`${name}-${option.value}`}
                  type="radio"
                  value={option.value}
                  disabled={disabled}
                  {...register(name, rules)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                />
                <label
                  htmlFor={`${name}-${option.value}`}
                  className="ml-2 block text-sm text-gray-700"
                >
                  {option.label}
                </label>
              </div>
            ))}
          </div>
        );
        
      case 'checkbox':
        return (
          <div className="space-y-2">
            {options.map((option) => (
              <div key={option.value} className="flex items-center">
                <input
                  id={`${name}-${option.value}`}
                  type="checkbox"
                  value={option.value}
                  disabled={disabled}
                  {...register(name, rules)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label
                  htmlFor={`${name}-${option.value}`}
                  className="ml-2 block text-sm text-gray-700"
                >
                  {option.label}
                </label>
              </div>
            ))}
          </div>
        );
        
      case 'number':
        return (
          <input
            id={name}
            type="number"
            {...register(name, {
              ...rules,
              valueAsNumber: true,
            })}
            placeholder={placeholder}
            disabled={disabled}
            className={`input ${errorMessage ? 'border-red-500 focus:ring-red-500' : ''}`}
          />
        );
        
      case 'decimal3':
        return (
          <NumericFormat
            id={name}
            name={name}
            value={watch(name)}
            onValueChange={(values) => setValue(name, values.floatValue)}
            thousandSeparator=""
            decimalSeparator=","
            allowNegative={false}
            placeholder={placeholder}
            disabled={disabled}
            decimalScale={3}
            fixedDecimalScale
            isAllowed={(values) => {
              const {formattedValue, floatValue} = values;
              if (formattedValue === '' || floatValue === undefined) return true;

              // Permite digitação parcial (ex: '0,' ou '0,1')
              if (!formattedValue.includes(',')) return true;

              // Validação final apenas quando completo
              // maxDecimalValue = null/undefined desativa o limite superior
              if (maxDecimalValue === null || maxDecimalValue === undefined) {
                return floatValue >= 0;
              }
              return floatValue >= 0 && floatValue <= maxDecimalValue;
            }}
            className={`input ${errorMessage ? 'border-red-500 focus:ring-red-500' : ''}`}
          />
        );

      case 'date':
        return (
          <input
            id={name}
            type="date"
            {...register(name, rules)}
            placeholder={placeholder}
            disabled={disabled}
            className={`input ${errorMessage ? 'border-red-500 focus:ring-red-500' : ''}`}
          />
        );
        
      case 'currency':
        return (
          <NumericFormat
            id={name}
            name={name}
            value={watch(name)}
            onValueChange={(values) => setValue(name, values.floatValue)}
            thousandSeparator="."
            decimalSeparator=","
            allowNegative={false}
            placeholder={placeholder}
            disabled={disabled}
            prefix="R$ "
            decimalScale={2}
            fixedDecimalScale
            className={`input ${errorMessage ? 'border-red-500 focus:ring-red-500' : ''}`}
          />
        );
        
      case 'percent':
        return (
          <NumericFormat
            id={name}
            name={name}
            value={watch(name)}
            onValueChange={(values) => setValue(name, values.floatValue)}
            thousandSeparator=""
            decimalSeparator=","
            allowNegative={false}
            placeholder={placeholder}
            disabled={disabled}
            suffix="%"
            decimalScale={1}
            fixedDecimalScale
            isAllowed={(values) => {
              const {formattedValue, floatValue} = values;
              if (formattedValue === '' || floatValue === undefined) return true;
              return floatValue >= 0 && floatValue <= 100;
            }}
            className={`input ${errorMessage ? 'border-red-500 focus:ring-red-500' : ''}`}
          />
        );
        
      default:
        return (
          <input
            id={name}
            type={type}
            {...register(name, rules)}
            placeholder={placeholder}
            disabled={disabled}
            className={`input ${errorMessage ? 'border-red-500 focus:ring-red-500' : ''}`}
          />
        );
    }
  };
  
  return (
    <div className={`form-group ${className}`}>
      <label htmlFor={name} className="form-label">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      
      {renderField()}
      
      {helpText && !errorMessage && (
        <p className="mt-1 text-sm text-gray-500">{helpText}</p>
      )}
      
      {errorMessage && (
        <div className="form-error flex items-center">
          <FiAlertCircle className="h-4 w-4 mr-1" />
          <span>{errorMessage}</span>
        </div>
      )}
    </div>
  );
};

export default FormField;
